(() => {
  'use strict';

  const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
  const CHART = {
    S: [18, 28], M: [20, 29], L: [22, 30],
    XL: [24, 31], '2XL': [26, 32], '3XL': [28, 33]
  };

  const normalizeSize = (value) => {
    const cleaned = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
    const aliases = { XXL: '2XL', XXXL: '3XL', 'XXXL': '3XL', '2X': '2XL', '3X': '3XL' };
    return aliases[cleaned] || cleaned;
  };

  const getFocusable = (container) => Array.from(container.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter((element) => !element.hidden && element.offsetParent !== null);

  const initFinder = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const overlay = root.querySelector('[data-size-finder-overlay]');
    const modal = overlay.querySelector('[role="dialog"]');
    const openButton = root.querySelector('[data-size-finder-open]');
    const closeButton = overlay.querySelector('[data-size-finder-close]');
    const form = overlay.querySelector('[data-size-finder-form]');
    const error = overlay.querySelector('[data-size-finder-error]');
    const result = overlay.querySelector('[data-size-finder-result]');
    const resultSize = overlay.querySelector('[data-size-finder-result-size]');
    const resultCopy = overlay.querySelector('[data-size-finder-result-copy]');
    const useSizeButton = overlay.querySelector('[data-size-finder-use-size]');
    const imperialFields = overlay.querySelector('[data-size-finder-imperial]');
    const metricFields = overlay.querySelector('[data-size-finder-metric]');
    const chartBody = overlay.querySelector('[data-size-finder-chart]');
    const productContext = root.closest('product-info') || root.closest('.shopify-section') || document;
    const initialVariantPicker = root.closest('variant-selects');
    const overlayId = `${modal.id}-Overlay`;
    const staleOverlay = document.getElementById(overlayId);
    if (staleOverlay && staleOverlay !== overlay) staleOverlay.remove();
    overlay.id = overlayId;
    overlay.classList.add('selfhoot-size-finder');
    document.body.appendChild(overlay);
    let currentUnit = 'imperial';
    let chartUnit = 'in';
    let recommendation = '';
    let lastFocused = null;

    const getVariantPicker = () => productContext.querySelector?.('variant-selects') || initialVariantPicker || document;
    const getSizeControls = () => Array.from(getVariantPicker().querySelectorAll('input[type="radio"], select option'))
      .filter((control) => {
        const name = control.name || control.parentElement?.name || '';
        return name.toLowerCase().includes('size') || SIZE_ORDER.includes(normalizeSize(control.value));
      });

    const getAvailableSizes = () => {
      const values = getSizeControls().map((control) => normalizeSize(control.value));
      return SIZE_ORDER.filter((size) => values.includes(size));
    };

    const renderChart = () => {
      const available = getAvailableSizes();
      const sizes = available.length ? available : SIZE_ORDER.slice(1);
      chartBody.textContent = '';
      sizes.forEach((size) => {
        if (!CHART[size]) return;
        const [chest, length] = CHART[size];
        const values = chartUnit === 'cm'
          ? [(chest * 2.54).toFixed(1), (length * 2.54).toFixed(1)]
          : [String(chest), String(length)];
        const row = document.createElement('tr');
        [size, ...values].forEach((value) => {
          const cell = document.createElement('td');
          cell.textContent = value;
          row.appendChild(cell);
        });
        chartBody.appendChild(row);
      });
    };

    const open = () => {
      lastFocused = document.activeElement;
      renderChart();
      overlay.hidden = false;
      document.body.classList.add('selfhoot-size-finder-open');
      requestAnimationFrame(() => modal.focus());
    };

    const close = () => {
      overlay.hidden = true;
      document.body.classList.remove('selfhoot-size-finder-open');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    };

    const setTab = (name) => {
      overlay.querySelectorAll('[data-size-finder-tab]').forEach((button) => {
        const active = button.dataset.sizeFinderTab === name;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
      overlay.querySelectorAll('[data-size-finder-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sizeFinderPanel !== name;
      });
      if (name === 'measure') renderChart();
    };

    const setUnits = (unit) => {
      currentUnit = unit;
      imperialFields.hidden = unit !== 'imperial';
      metricFields.hidden = unit !== 'metric';
      overlay.querySelectorAll('[data-size-finder-unit]').forEach((button) => {
        const active = button.dataset.sizeFinderUnit === unit;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const readMeasurements = () => {
      if (currentUnit === 'imperial') {
        const feet = Number(overlay.querySelector('[data-size-finder-feet]').value);
        const inches = Number(overlay.querySelector('[data-size-finder-inches]').value || 0);
        const pounds = Number(overlay.querySelector('[data-size-finder-pounds]').value);
        if (!feet || inches < 0 || inches > 11 || !pounds) return null;
        return { heightIn: feet * 12 + inches, pounds };
      }
      const cm = Number(overlay.querySelector('[data-size-finder-centimeters]').value);
      const kg = Number(overlay.querySelector('[data-size-finder-kilograms]').value);
      if (!cm || !kg) return null;
      return { heightIn: cm / 2.54, pounds: kg * 2.2046226218 };
    };

    const estimateBaseSize = ({ heightIn, pounds }) => {
      const normalizedWeight = pounds - ((heightIn - 69) * 4.5);
      if (normalizedWeight <= 115) return 'XS';
      if (normalizedWeight <= 140) return 'S';
      if (normalizedWeight <= 170) return 'M';
      if (normalizedWeight <= 205) return 'L';
      if (normalizedWeight <= 240) return 'XL';
      if (normalizedWeight <= 285) return '2XL';
      return '3XL';
    };

    const nearestAvailable = (target, available) => {
      const targetIndex = SIZE_ORDER.indexOf(target);
      return available.slice().sort((a, b) => {
        const da = Math.abs(SIZE_ORDER.indexOf(a) - targetIndex);
        const db = Math.abs(SIZE_ORDER.indexOf(b) - targetIndex);
        return da - db || SIZE_ORDER.indexOf(a) - SIZE_ORDER.indexOf(b);
      })[0];
    };

    const calculate = () => {
      const available = getAvailableSizes();
      if (!available.length) return { error: 'We could not read this tee’s available sizes. Please use the size chart instead.' };
      const measurements = readMeasurements();
      if (!measurements) return { error: 'Add your height and weight so we have somewhere honest to start.' };

      const base = estimateBaseSize(measurements);
      const fit = form.querySelector('input[type="radio"]:checked')?.value || 'relaxed';
      const shift = fit === 'closer' ? -1 : fit === 'oversized' ? 1 : 0;
      const baseIndex = SIZE_ORDER.indexOf(base);
      const shifted = SIZE_ORDER[Math.max(0, Math.min(SIZE_ORDER.length - 1, baseIndex + shift))];
      const selected = available.includes(shifted) ? shifted : nearestAvailable(shifted, available);
      const availabilityNote = selected !== shifted ? ` ${shifted} is not offered here, so ${selected} is the closest available option.` : '';
      return { size: selected, copy: `A starting point for a ${fit} fit.${availabilityNote} Bodies and preferences vary, so compare a favorite tee when you can.` };
    };

    const selectSize = (size) => {
      const normalized = normalizeSize(size);
      const variantPicker = getVariantPicker();
      const radio = Array.from(variantPicker.querySelectorAll('input[type="radio"]')).find((input) =>
        (input.name || '').toLowerCase().includes('size') && normalizeSize(input.value) === normalized
      );
      if (radio && !radio.disabled) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        radio.click();
        close();
        return true;
      }
      const select = Array.from(variantPicker.querySelectorAll('select')).find((element) =>
        (element.name || '').toLowerCase().includes('size') && Array.from(element.options).some((option) => normalizeSize(option.value) === normalized)
      );
      if (select) {
        const option = Array.from(select.options).find((item) => normalizeSize(item.value) === normalized);
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close();
        return true;
      }
      return false;
    };

    openButton.addEventListener('click', open);
    closeButton.addEventListener('click', close);
    overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
    overlay.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key !== 'Tab') return;
      const focusable = getFocusable(modal);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    overlay.querySelectorAll('[data-size-finder-tab]').forEach((button) => button.addEventListener('click', () => setTab(button.dataset.sizeFinderTab)));
    overlay.querySelectorAll('[data-size-finder-unit]').forEach((button) => button.addEventListener('click', () => setUnits(button.dataset.sizeFinderUnit)));
    overlay.querySelectorAll('[data-chart-unit]').forEach((button) => button.addEventListener('click', () => {
      chartUnit = button.dataset.chartUnit;
      overlay.querySelectorAll('[data-chart-unit]').forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      renderChart();
    }));
    overlay.querySelector('[data-size-finder-back]').addEventListener('click', () => setTab('suggest'));

    // Keep calculator-only events isolated from Shopify's product variant logic.
    ['input', 'change'].forEach((eventName) => {
      form.addEventListener(eventName, (event) => {
        event.stopPropagation();
        result.hidden = true;
      });
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      error.hidden = true;
      const answer = calculate();
      if (answer.error) {
        error.textContent = answer.error;
        error.hidden = false;
        error.focus?.();
        return;
      }
      recommendation = answer.size;
      resultSize.textContent = answer.size;
      resultCopy.textContent = answer.copy;
      useSizeButton.textContent = `USE SIZE ${answer.size}`;
      useSizeButton.disabled = false;
      result.hidden = false;
      requestAnimationFrame(() => result.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
    });

    useSizeButton.addEventListener('click', () => {
      if (!selectSize(recommendation)) {
        useSizeButton.disabled = true;
        resultCopy.textContent = 'That size is not currently selectable for this color. Try another color or compare measurements.';
      }
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-selfhoot-size-finder]').forEach(initFinder);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
